import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { formatCr, formatINRFull } from "@/lib/format";
import { SourceBadge } from "@/components/v3/SourceBadge";
import { LoadingState, EmptyState, ErrorState } from "@/components/v3/States";
import { DisbursementTab, ServicingTab } from "@/components/v3/ServicingTabs";
import { Loan360 } from "@/components/v3/Loan360";
import { VerificationTab } from "@/components/v3/VerificationTab";
import { DocumentsTab } from "@/components/v3/DocumentsTab";
import { PortalAccess } from "@/components/v3/PortalAccess";
import { LoanFinancialReview } from "@/components/v3/LoanFinancialReview";

const TABS = ["Overview", "Business", "Promoters", "Verification", "Financials", "Banking", "GST", "Bureau", "Existing Debt",
  "Collateral", "Documents", "AI Analysis", "Scorecard", "Credit Memo", "Approvals", "CP/Documentation",
  "Disbursement", "Servicing", "Audit & Payments", "Activity"];
const inputCls = "w-full border border-slate-300 rounded px-2.5 py-1.5 text-sm";
const KV = ({ k, v, src }) => (
  <div className="flex justify-between gap-3 border-b border-slate-100 py-1.5 text-sm">
    <span className="text-slate-400">{k}</span>
    <span className="font-semibold text-slate-800 text-right">{v ?? "—"}{src && <span className="block text-[10px] font-normal text-slate-400">source: {src}</span>}</span>
  </div>
);
const Sec = ({ title, right, children }) => (
  <div className="bg-white border border-slate-200 rounded-sm p-5 mb-4">
    <div className="flex items-center gap-2 mb-3"><span className="font-semibold text-slate-900 text-sm">{title}</span>{right}</div>
    {children}
  </div>
);
const NA = ({ what }) => <EmptyState title="Not available" detail={`No ${what} data exists in the loan file. Nothing is fabricated.`} />;

const FIGS = [["revenue", "Revenue"], ["ebitda", "EBITDA"], ["pat", "PAT"], ["net_worth", "Net worth"],
  ["total_debt", "Total debt"], ["interest_expense", "Interest expense"], ["annual_debt_service", "Annual debt service"],
  ["current_assets", "Current assets"], ["current_liabilities", "Current liabilities"], ["wc_days", "WC cycle (days)"],
  ["gst_revenue", "GST revenue"], ["bank_credits", "Bank credits"]];

function Financials({ lf, id, reload }) {
  const saved = (lf.overlays.financial_analyses || [])[0];
  const [figs, setFigs] = useState(saved?.figures || {});
  const upd = (k, field, v) => setFigs({ ...figs, [k]: { ...(figs[k] || {}), [field]: v } });
  const save = () => api.put(`/v3/loanfile/${id}/financials`, { figures: figs })
    .then(() => { toast.success("Financial analysis saved"); reload(); })
    .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  const a = saved?.analysis;
  return (
    <>
      <Sec title="Evidence-linked figures" right={<SourceBadge source="TEST_V3" />}>
        <div className="grid sm:grid-cols-2 gap-2">
          {FIGS.map(([k, label]) => (
            <div key={k} className="flex gap-2 items-center">
              <span className="w-36 text-[11px] font-bold uppercase tracking-wide text-slate-400 shrink-0">{label}</span>
              <input data-testid={`fin-${k}`} type="number" className={inputCls} placeholder="value"
                value={figs[k]?.value ?? ""} onChange={(e) => upd(k, "value", e.target.value)} />
              <input data-testid={`fin-${k}-src`} className={inputCls} placeholder="source (e.g. FY26 Audited FS)"
                value={figs[k]?.source ?? ""} onChange={(e) => upd(k, "source", e.target.value)} />
            </div>
          ))}
        </div>
        <button data-testid="fin-save-btn" onClick={save} className="mt-3 bg-[#b3122a] text-white text-sm font-semibold rounded px-4 py-2">Save & compute</button>
      </Sec>
      {a && (
        <Sec title="Ratios & scenarios">
          {a.discrepancies?.length > 0 && (
            <div className="mb-3 border border-amber-300 bg-amber-50 rounded p-2.5 text-[12px] text-amber-800" data-testid="fin-discrepancies">
              {a.discrepancies.map((d) => <div key={d}>⚠ {d}</div>)}
            </div>
          )}
          <table className="w-full text-sm" data-testid="scenario-table">
            <thead><tr className="text-[11px] uppercase text-slate-400 border-b border-slate-200">
              <th className="text-left py-1.5">Metric</th><th className="text-right">Base</th><th className="text-right">Downside</th><th className="text-right">Severe</th></tr></thead>
            <tbody>
              {[["DSCR", "dscr"], ["ICR", "icr"], ["Debt/EBITDA", "debt_ebitda"]].map(([l, k]) => (
                <tr key={k} className="border-b border-slate-100"><td className="py-1.5 text-slate-500">{l}</td>
                  <td className="text-right font-bold tabular-nums" data-testid={`base-${k}`}>{a.base[k] ?? "—"}</td>
                  <td className="text-right tabular-nums">{a.downside[k] ?? "—"}</td>
                  <td className="text-right tabular-nums text-[#b3122a]">{a.severe_downside[k] ?? "—"}</td></tr>
              ))}
            </tbody>
          </table>
          <div className="grid grid-cols-3 gap-2 mt-3 text-sm">
            <KV k="D/E" v={a.base.de} /><KV k="Current ratio" v={a.base.current_ratio} /><KV k="WC cycle" v={a.base.wc_cycle_days} />
          </div>
        </Sec>
      )}
    </>
  );
}

function Collateral({ lf, id, reload }) {
  const saved = (lf.overlays.collateral_analyses || [])[0];
  const [f, setF] = useState(saved || { owner_type: "borrower", exposure: lf.header.requested || 0 });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = () => api.put(`/v3/loanfile/${id}/collateral-analysis`, {
    ...f, market_value: Number(f.market_value) || 0, distress_value: Number(f.distress_value) || 0, exposure: Number(f.exposure) || 0,
  }).then(() => { toast.success("Collateral analysis saved"); reload(); })
    .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  return (
    <>
      {saved?.third_party && (
        <div className="mb-4 border-2 border-[#b3122a] bg-red-50 rounded-sm p-4 text-center" data-testid="third-party-banner">
          <div className="text-lg font-black text-[#b3122a] tracking-wide">THIRD-PARTY COLLATERAL</div>
          <div className="text-[12px] text-red-700 mt-1">Enhanced diligence required: owner identity, relationship, rationale, consent, guarantee, title verification, direct confirmation, legal approval.</div>
        </div>
      )}
      <Sec title="Collateral analysis" right={<SourceBadge source="TEST_V3" />}>
        <div className="grid sm:grid-cols-2 gap-3">
          <input data-testid="col-owner" className={inputCls} placeholder="Owner name" value={f.owner || ""} onChange={set("owner")} />
          <select data-testid="col-owner-type" className={inputCls} value={f.owner_type} onChange={set("owner_type")}>
            <option value="borrower">Borrower</option><option value="promoter">Promoter</option><option value="third_party">Third party</option>
          </select>
          <input data-testid="col-type" className={inputCls} placeholder="Type (e.g. Commercial property)" value={f.type || ""} onChange={set("type")} />
          <input data-testid="col-location" className={inputCls} placeholder="Location" value={f.location || ""} onChange={set("location")} />
          <input data-testid="col-market" type="number" className={inputCls} placeholder="Market value ₹" value={f.market_value || ""} onChange={set("market_value")} />
          <input data-testid="col-distress" type="number" className={inputCls} placeholder="Distress value ₹" value={f.distress_value || ""} onChange={set("distress_value")} />
          <input data-testid="col-exposure" type="number" className={inputCls} placeholder="Exposure ₹" value={f.exposure || ""} onChange={set("exposure")} />
          <input data-testid="col-charge" className={inputCls} placeholder="Existing charge / lender" value={f.existing_charge || ""} onChange={set("existing_charge")} />
          <input data-testid="col-title" className={inputCls} placeholder="Title / legal status" value={f.title_status || ""} onChange={set("title_status")} />
          <input data-testid="col-source" className={inputCls} placeholder="Source (e.g. Valuation report 2026)" value={f.source || ""} onChange={set("source")} />
        </div>
        <button data-testid="col-save-btn" onClick={save} className="mt-3 bg-[#b3122a] text-white text-sm font-semibold rounded px-4 py-2">Save & compute covers</button>
        {saved && (
          <div className="grid grid-cols-2 gap-3 mt-4">
            <div className="border border-slate-200 rounded p-3 text-center"><div className="ceo-label">Market cover</div><div className="ceo-fig text-2xl" data-testid="market-cover">{saved.market_cover ?? "—"}x</div></div>
            <div className="border border-slate-200 rounded p-3 text-center"><div className="ceo-label">Distress cover</div><div className="ceo-fig text-2xl" data-testid="distress-cover">{saved.distress_cover ?? "—"}x</div></div>
          </div>
        )}
      </Sec>
    </>
  );
}

const NEG = [["no_collateral", "No collateral / unsecured"], ["negative_promoter_net_worth", "Negative promoter net worth"],
  ["no_gst_filing", "No GST filing"], ["no_clear_end_use", "No clear end use"], ["turnover_below_2cr", "Turnover < ₹2 Cr"],
  ["group_turnover_ratio_below_1", "Group turnover/loan < 1"], ["emi_affordability_low", "EMI affordability < ₹3L/Cr"]];
const SECS = [["business_quality", 20], ["purpose", 15], ["collateral_quality", 20], ["track_record", 15], ["promoter_net_worth", 15], ["customer_stability", 15]];

function Scorecard({ lf, id, reload }) {
  const saved = (lf.overlays.scorecards || [])[0];
  const [neg, setNeg] = useState(saved?.negative || {});
  const [secs, setSecs] = useState(saved?.sections || {});
  const save = () => api.post(`/v3/loanfile/${id}/scorecard`, { negative: neg, sections: secs, reasons: [] })
    .then((r) => { toast.success(`Score ${r.data.scorecard.total_score}/100 · grade ${r.data.scorecard.grade}`); reload(); })
    .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  return (
    <Sec title="Business Loan Score Card (BL-2026-01)" right={<SourceBadge source="TEST_V3" />}>
      <div className="text-[11px] font-bold uppercase text-slate-400 mb-2">Negative list — any YES rejects</div>
      <div className="grid sm:grid-cols-2 gap-1 mb-4">
        {NEG.map(([k, l]) => (
          <label key={k} className="flex items-center gap-2 text-[13px] text-slate-700">
            <input type="checkbox" data-testid={`neg-${k}`} checked={!!neg[k]} onChange={(e) => setNeg({ ...neg, [k]: e.target.checked })} /> {l}
          </label>
        ))}
      </div>
      <div className="space-y-2 mb-4">
        {SECS.map(([k, mx]) => (
          <div key={k} className="flex items-center gap-3">
            <span className="w-44 text-[12px] text-slate-500 capitalize">{k.replaceAll("_", " ")} (max {mx})</span>
            <input data-testid={`sc-${k}`} type="number" min="0" max={mx} className="w-20 border border-slate-300 rounded px-2 py-1 text-sm tabular-nums"
              value={secs[k] ?? ""} onChange={(e) => setSecs({ ...secs, [k]: Math.min(mx, Number(e.target.value)) })} />
          </div>
        ))}
      </div>
      <button data-testid="sc-save-btn" onClick={save} className="bg-[#b3122a] text-white text-sm font-semibold rounded px-4 py-2">Compute score</button>
      {saved && (
        <div className="mt-4 border border-slate-200 rounded p-4 flex items-center gap-6" data-testid="scorecard-result">
          <div><div className="ceo-label">Score</div><div className="ceo-fig text-3xl">{saved.total_score}/100</div></div>
          <div><div className="ceo-label">Grade</div><div className="ceo-fig text-3xl">{saved.grade}</div></div>
          <div><div className="ceo-label">Rate band</div><div className="text-sm font-bold">{saved.interest_band}</div></div>
          <div className="text-[11px] text-slate-400 ml-auto max-w-[180px]">{saved.note}</div>
        </div>
      )}
    </Sec>
  );
}

function Decisions({ lf, id, reload, role }) {
  const [comments, setComments] = useState("");
  const [conditions, setConditions] = useState("");
  const decs = lf.overlays.decisions || [];
  const act = (step, action) => api.post(`/v3/loanfile/${id}/decision`, {
    step, action, comments, conditions: conditions ? conditions.split(";").map((c) => c.trim()).filter(Boolean) : [],
  }).then(() => { toast.success(`${step} → ${action}`); setComments(""); reload(); })
    .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  const h = lf.header;
  const fin = (lf.overlays.financial_analyses || [])[0];
  const col = (lf.overlays.collateral_analyses || [])[0];
  return (
    <>
      <Sec title="Committee decision summary — one page" right={<SourceBadge source={lf.core.record_source === "HISTORICAL" ? "HISTORICAL" : "TEST_V3"} />}>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4" data-testid="committee-summary">
          <KV k="Borrower" v={h.company} /><KV k="Amount" v={h.requested != null ? formatINRFull(h.requested) : "—"} />
          <KV k="Product" v={h.product} /><KV k="DSCR (base)" v={h.dscr} />
          <KV k="Score / grade" v={h.score != null ? `${h.score} · ${h.risk_grade}` : "—"} />
          <KV k="Market cover" v={h.market_cover ? `${h.market_cover}x` : "—"} />
          <KV k="Collateral" v={col?.type} /><KV k="Third-party" v={h.third_party_collateral ? "YES ⚠" : "No"} />
          <KV k="Credit rec." v={decs.find((d) => d.step === "credit")?.action} />
          <KV k="Risk rec." v={decs.find((d) => d.step === "risk")?.action} />
          <KV k="Exceptions" v={(h.exceptions || []).length || "None"} />
          <KV k="Downside DSCR" v={fin?.analysis?.downside?.dscr} />
          <KV k="Verification" v={h.verification ? `${h.verification.pass} pass · ${h.verification.fail} fail · ${Math.max(12 - h.verification.runs, 0)} not run` : "—"} />
          <KV k="Documents" v={`${h.documents ?? 0} on file · ${h.esigned ?? 0} e-signed`} />
        </div>
        {h.verification?.fail > 0 && <div className="mt-2 text-[12px] text-[#b3122a] font-semibold" data-testid="committee-verif-warning">⚠ {h.verification.fail} verification check(s) FAILED — soft gate, review before deciding.</div>}
      </Sec>
      <Sec title="Actions">
        <textarea data-testid="decision-comments" rows={2} className={inputCls} placeholder="Comments…" value={comments} onChange={(e) => setComments(e.target.value)} />
        <input data-testid="decision-conditions" className={`${inputCls} mt-2`} placeholder="Conditions (semicolon-separated, committee only)" value={conditions} onChange={(e) => setConditions(e.target.value)} />
        <div className="mt-3 space-y-2">
          {[["credit", ["approve", "request_info", "refer", "decline"]], ["risk", ["approve", "request_info", "refer", "decline"]],
            ["committee", ["approve", "approve_with_conditions", "refer", "decline"]]].map(([step, actions]) => (
            <div key={step} className="flex items-center gap-2 flex-wrap">
              <span className="w-24 text-[11px] font-bold uppercase text-slate-400">{step}</span>
              {actions.map((a) => (
                <button key={a} data-testid={`decision-${step}-${a}`} onClick={() => act(step, a)}
                  className={`text-[11px] font-bold uppercase rounded px-2.5 py-1.5 border ${a.includes("approve") ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50" : a === "decline" ? "border-red-300 text-red-700 hover:bg-red-50" : "border-slate-300 text-slate-600 hover:bg-slate-50"}`}>
                  {a.replaceAll("_", " ")}
                </button>
              ))}
            </div>
          ))}
        </div>
        <div className="text-[11px] text-slate-400 mt-2">Server enforces role per step (credit/risk/committee). Your role: <b>{role}</b></div>
      </Sec>
      <Sec title={`Decision history (${decs.length})`}>
        {decs.length === 0 ? <NA what="decision" /> : decs.map((d) => (
          <div key={d.v3_id} className="border-b border-slate-100 py-2 text-[12px]" data-testid="decision-history-row">
            <b className="uppercase">{d.step}</b> · <span className="font-bold text-[#b3122a]">{d.action}</span> v{d.version} — {d.decided_by.name} ({d.decided_by.role}) · {new Date(d.decided_at).toLocaleString()}
            {d.comments && <div className="text-slate-500">“{d.comments}”</div>}
            {d.conditions?.length > 0 && <div className="text-amber-700">Conditions: {d.conditions.join("; ")}</div>}
          </div>
        ))}
      </Sec>
    </>
  );
}

function CpTab({ lf, id, reload }) {
  const cp = lf.cp_checklist;
  if (!cp) return <NA what="CP checklist (created on committee approval)" />;
  const ready = lf.header.ready_to_disburse;
  const setSt = (code, status) => api.patch(`/v3/loanfile/${id}/cp/${code}`, { status })
    .then(() => reload()).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  return (
    <>
      <div className={`mb-4 rounded-sm p-4 text-center border-2 ${ready ? "border-emerald-500 bg-emerald-50" : "border-[#b3122a] bg-red-50"}`} data-testid="disburse-banner">
        <div className={`text-xl font-black tracking-wide ${ready ? "text-emerald-700" : "text-[#b3122a]"}`}>{ready ? "READY TO DISBURSE" : "BLOCKED"}</div>
        {!ready && <div className="text-[12px] text-red-700 mt-1" data-testid="blockers-list">{lf.header.blockers.join(" · ")}</div>}
      </div>
      {lf.term_sheet && (
        <Sec title="Mock sanction / term sheet" right={<SourceBadge source="MOCK" />}>
          <div className="grid grid-cols-3 gap-2">
            <KV k="Amount" v={lf.term_sheet.amount != null ? formatINRFull(lf.term_sheet.amount) : "—"} />
            <KV k="Rate band" v={lf.term_sheet.interest_band} /><KV k="Tenor" v={lf.term_sheet.tenor_months ? `${lf.term_sheet.tenor_months} mo` : "—"} />
          </div>
          {lf.term_sheet.conditions?.length > 0 && <div className="text-[12px] text-amber-700 mt-1">Conditions: {lf.term_sheet.conditions.join("; ")}</div>}
        </Sec>
      )}
      <Sec title="Conditions precedent">
        {cp.items.map((i) => (
          <div key={i.code} className="flex items-center gap-2 border-b border-slate-100 py-2" data-testid={`cp-item-${i.code}`}>
            <span className={`w-2 h-2 rounded-full shrink-0 ${i.status === "met" ? "bg-emerald-500" : i.status === "waived" ? "bg-slate-400" : "bg-[#b3122a]"}`} />
            <span className="text-sm text-slate-800 flex-1">{i.description} <span className="text-[10px] text-slate-400">({i.code})</span></span>
            {["pending", "met", "waived"].map((s) => (
              <button key={s} data-testid={`cp-${i.code}-${s}`} onClick={() => setSt(i.code, s)}
                className={`text-[10px] font-bold uppercase rounded px-1.5 py-1 border ${i.status === s ? "border-[#111111] bg-[#111111] text-white" : "border-slate-200 text-slate-400"}`}>{s}</button>
            ))}
          </div>
        ))}
      </Sec>
    </>
  );
}

export default function LoanWorkspace() {
  const { id } = useParams();
  const [lf, setLf] = useState(null);
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState("Overview");
  const [aiBusy, setAiBusy] = useState(false);
  const role = JSON.parse(sessionStorage.getItem("rf_role") || "null");

  const reload = useCallback(() => {
    api.get(`/v3/loanfile/${id}`).then((r) => setLf(r.data))
      .catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, [id]);
  useEffect(() => { setLf(null); reload(); }, [reload]);

  if (err) return <div className="p-8"><ErrorState message={err} onRetry={reload} /></div>;
  if (!lf) return <div className="p-8"><LoadingState label="Composing loan file…" /></div>;
  const h = lf.header;
  const app = lf.core.application || {};
  const isHist = lf.core.record_source === "HISTORICAL";
  const genAI = () => {
    setAiBusy(true);
    api.post(`/v3/loanfile/${id}/ai-summary`).then(() => { toast.success("AI summary generated"); reload(); })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => setAiBusy(false));
  };
  const ai = (lf.overlays.ai_summaries || [])[0];
  const kvDump = (obj, keys) => keys.filter((k) => obj[k] !== undefined && obj[k] !== null && obj[k] !== "").map((k) => <KV key={k} k={k} v={String(obj[k]).slice(0, 120)} />);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px]" data-testid="loan-workspace">
      <div className="bg-white border border-slate-200 rounded-sm p-5 mb-4" data-testid="workspace-header">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-[12px] text-slate-400">{h.loan_id}</span>
          <h1 className="ceo-fig text-2xl text-[#111111]">{h.company}</h1>
          <SourceBadge source={lf.core.record_source} />
          {h.third_party_collateral && <span className="bg-red-50 border border-[#b3122a] text-[#b3122a] text-[10px] font-black rounded px-2 py-0.5" data-testid="header-third-party">THIRD-PARTY COLLATERAL</span>}
          {h.ready_to_disburse != null && (
            <span className={`text-[10px] font-black rounded px-2 py-0.5 border ${h.ready_to_disburse ? "bg-emerald-50 border-emerald-500 text-emerald-700" : "bg-red-50 border-[#b3122a] text-[#b3122a]"}`} data-testid="header-ready">
              {h.ready_to_disburse ? "READY TO DISBURSE" : "BLOCKED"}
            </span>
          )}
        </div>
        <div className="flex gap-5 flex-wrap mt-3 text-[12px]">
          {[["Requested", h.requested ? formatCr(h.requested) : "—"], ["Sanctioned", h.sanctioned ? formatCr(h.sanctioned) : "—"],
            ["Product", h.product], ["Stage", h.stage], ["Owner", h.owner || "—"], ["Compl.", h.completeness != null ? `${h.completeness}%` : "—"],
            ["Grade", h.risk_grade || "—"], ["DSCR", h.dscr ?? "—"], ["MV cover", h.market_cover ? `${h.market_cover}x` : "—"],
            ["Exceptions", (h.exceptions || []).length],
            ["Verified", h.verification ? `${h.verification.pass}/${h.verification.runs}${h.verification.fail ? ` · ${h.verification.fail} FAIL` : ""}` : "—"],
            ["Docs", `${h.documents ?? 0} · ${h.esigned ?? 0} signed`]].map(([k, v]) => (
            <span key={k}><span className="text-slate-400 uppercase text-[10px] font-bold">{k} </span><b className="text-slate-800 tabular-nums">{v}</b></span>
          ))}
        </div>
      </div>

      <select className="rf-input mb-4 sm:hidden no-print" data-testid="workspace-mobile-tab" aria-label="Loan file section" value={tab} onChange={e => setTab(e.target.value)}>{TABS.map(t => <option key={t}>{t}</option>)}</select>
      <div className="hidden sm:flex flex-wrap gap-1 pb-2 mb-4 no-print" data-testid="workspace-tabs">
        {TABS.map((t) => (
          <button key={t} data-testid={`tab-${t.replaceAll(/[/ ]/g, "-").toLowerCase()}`} onClick={() => setTab(t)}
            className={`shrink-0 text-[11px] font-bold uppercase tracking-wide rounded px-2.5 py-1.5 border whitespace-nowrap ${tab === t ? "border-[#b3122a] text-[#b3122a] bg-[#b3122a]/5" : "border-slate-200 bg-white text-slate-500"}`}>{t}</button>
        ))}
      </div>

      {tab === "Overview" && (
        <Sec title="Key facts" right={<SourceBadge source={lf.core.record_source} />}>
          <div className="grid sm:grid-cols-2 gap-x-6">
            {isHist ? kvDump(app, ["company_Name", "applicant_no", "status", "applicant_type", "loan_type", "applied_loan_amt", "applied_tenor", "loan_purpose", "GST_no", "company_PAN", "city", "state", "years_in_business", "facility_number", "ls_loan_amount", "ls_interest_rate", "ls_tenor"]) :
              kvDump({ ...(app.sections?.company || {}), stage: app.stage, note: app.note }, ["company_Name", "entity_type", "pan", "gstin", "city", "years_in_business", "turnover", "amount", "purpose", "tenor", "stage"])}
          </div>
          {isHist && <div className="text-[11px] text-slate-400 mt-2">Historical record — read-only. V3 analysis below never modifies it.</div>}
        </Sec>
      )}
      {tab === "Overview" && (
        <PortalAccess id={id} role={role} />
      )}
      {tab === "Business" && <Sec title="Business"><div className="grid sm:grid-cols-2 gap-x-6">{isHist ? kvDump(app, ["business_nature", "business_category", "company_type", "company_CIN", "monthly_net_income", "company_debt", "company_equity", "NPA_in_last_3_years"]) : kvDump(app.sections?.company || {}, ["entity_type", "cin", "turnover", "years_in_business", "purpose"])}</div></Sec>}
      {tab === "Promoters" && (
        <Sec title="Promoters" right={<SourceBadge source={lf.core.record_source} />}>
          {isHist ? ((lf.core.promoters || []).length ? lf.core.promoters.map((p, i) => <div key={i} className="border-b border-slate-100 py-2 text-sm"><b>{p.name}</b> · net worth {p.net_worth ? formatCr(p.net_worth) : "—"} · {p.email_id || ""}</div>) : <NA what="promoter" />)
            : ((app.sections?.promoters || []).length ? app.sections.promoters.map((p, i) => <div key={i} className="border-b border-slate-100 py-2 text-sm"><b>{p.name}</b> · net worth {p.net_worth ? formatCr(p.net_worth) : "—"}</div>) : <NA what="promoter" />)}
        </Sec>
      )}
      {tab === "Financials" && <Financials lf={lf} id={id} reload={reload} />}
      {tab === "Banking" && (isHist ? <NA what="banking" /> : (app.sections?.banking?.bank_name ? <Sec title="Banking"><div className="grid sm:grid-cols-2">{kvDump(app.sections.banking, ["bank_name", "avg_monthly_credits"])}</div><div className="mt-2"><SourceBadge source="MOCK" /> <span className="text-[11px] text-slate-400">AA analysis not connected</span></div></Sec> : <NA what="banking" />))}
      {tab === "GST" && (isHist ? (app.GST_no ? <Sec title="GST"><KV k="GSTIN" v={app.GST_no} src="legacy applicants" /></Sec> : <NA what="GST" />) : (app.sections?.gst?.gstin ? <Sec title="GST"><div className="grid sm:grid-cols-2">{kvDump(app.sections.gst, ["gstin", "filing_status"])}</div></Sec> : <NA what="GST" />))}
      {tab === "Bureau" && <NA what="bureau/CIC" />}
      {tab === "Existing Debt" && (!isHist && (app.sections?.existing_debt || []).length ? <Sec title="Existing debt">{app.sections.existing_debt.map((d, i) => <div key={i} data-testid={`existing-debt-${i}`} className="border-b border-slate-100 py-2 text-sm"><b>{d.lender}</b> · outstanding {formatINRFull(d.outstanding)} · EMI {formatINRFull(d.emi)}</div>)}</Sec> : app.sections?.no_existing_debt ? <Sec title="Existing debt"><div className="text-sm text-slate-600">No existing borrowings — declared by applicant.</div></Sec> : <NA what="existing debt" />)}
      {tab === "Collateral" && <Collateral lf={lf} id={id} reload={reload} />}
      {tab === "Documents" && <DocumentsTab id={id} role={role} reload={reload} />}
      {tab === "Verification" && <VerificationTab id={id} reload={reload} canRun={["admin", "credit", "risk", "ops"].includes(role)} />}
      {tab === "AI Analysis" && (
        <Sec title="AI Credit Copilot" right={<SourceBadge source="TEST_V3" />}>
          <button data-testid="ai-generate-btn" onClick={genAI} disabled={aiBusy}
            className="bg-[#111111] text-white text-sm font-semibold rounded px-4 py-2 disabled:opacity-50 mb-3">{aiBusy ? "Analyzing…" : "Generate evidence-linked summary"}</button>
          {ai ? <div className="prose prose-sm max-w-none whitespace-pre-wrap text-[13px] text-slate-700" data-testid="ai-summary-content">{ai.content_md}</div> : <div className="text-[12px] text-slate-400">AI summarizes only data present in this loan file — facts, interpretation, discrepancies and missing data are separated. It never invents bureau/GST/bank results.</div>}
        </Sec>
      )}
      {tab === "Scorecard" && <Scorecard lf={lf} id={id} reload={reload} />}
      {tab === "Credit Memo" && (ai ? <Sec title="Draft memo (from AI analysis)"><div className="whitespace-pre-wrap text-[13px] text-slate-700">{ai.content_md}</div><div className="text-[11px] text-slate-400 mt-2">Full versioned memo editor arrives in a later build.</div></Sec> : <NA what="credit memo (generate AI analysis first)" />)}
      {tab === "Approvals" && <Decisions lf={lf} id={id} reload={reload} role={role} />}
      {tab === "CP/Documentation" && <CpTab lf={lf} id={id} reload={reload} />}
      {tab === "Disbursement" && <DisbursementTab lf={lf} id={id} reload={reload} />}
      {tab === "Servicing" && <>{isHist && <Loan360 loanKey={id} />}<ServicingTab lf={lf} id={id} /></>}
      {tab === "Audit & Payments" && <LoanFinancialReview id={id} />}
      {tab === "Activity" && (
        <Sec title="Activity — V3 audit trail">
          <ActivityList loanKey={id} />
        </Sec>
      )}
    </div>
  );
}

function ActivityList({ loanKey }) {
  const [events, setEvents] = useState(null);
  useEffect(() => {
    api.get("/v3/audit").then((r) => setEvents(r.data.events.filter((e) =>
      String(e.entity_v3_id || "").includes(loanKey) || String(e.details || "").includes(loanKey)))).catch(() => setEvents([]));
  }, [loanKey]);
  if (!events) return <LoadingState />;
  if (!events.length) return <NA what="activity" />;
  return events.map((e) => (
    <div key={e.v3_id} className="border-b border-slate-100 py-1.5 text-[12px]" data-testid="activity-row">
      <b className="text-[#b3122a]">{e.action}</b> {e.entityType} — {e.performedBy?.email} ({e.performedBy?.role}) · {new Date(e.performedAt).toLocaleString()} · {e.details}
    </div>
  ));
}
